/** Telling receipts apart from other files someone drops or pastes. */

/**
 * What a receipt can be: a photo, a PDF, or an email saved from a mail app. iPhone photos are
 * named too, since Windows doesn't count .heic as an image; the server turns them into JPEGs.
 */
/** How a receipt came to be added, for the report's history. */
export type ReceiptVia = "Photo" | "Paste" | "Upload" | "Drop";

export const ReceiptAccept =
  "image/*,.heic,.heif,application/pdf,.eml,.msg,message/rfc822,application/vnd.ms-outlook";

export function isReceiptFile(file: File): boolean {
  return (
    file.type.startsWith("image/") ||
    file.type === "application/pdf" ||
    file.type === "message/rfc822" ||
    file.type === "application/vnd.ms-outlook" ||
    /\.(pdf|eml|msg|jpe?g|png|heic|heif|webp|gif)$/i.test(file.name)
  );
}

/** A pasted screenshot arrives as "image.png"; give it a name worth keeping. */
export function named(file: File): File {
  if (file.name && file.name !== "image.png" && file.name !== "blob") return file;
  const extension = file.type.split("/")[1]?.replace("jpeg", "jpg") ?? "png";
  return new File([file], `pasted-receipt.${extension}`, { type: file.type });
}

/** The first receipt among dropped or pasted files. */
export function firstReceipt(files: FileList | File[] | undefined | null): File | undefined {
  const found = Array.from(files ?? []).find(isReceiptFile);
  return found ? named(found) : undefined;
}

/** Every receipt among dropped or pasted files, in order. */
export function receiptFiles(files: FileList | File[] | undefined | null): File[] {
  return Array.from(files ?? [])
    .filter(isReceiptFile)
    .map(named);
}
